import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, Trash2 } from 'lucide-react';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Input } from '@/shared/components/ui/input';
import { Switch } from '@/shared/components/ui/switch';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { cn } from '@/core/lib/utils';
import { FieldOptionsEditor } from './FieldOptionsEditor';
import { LinearScaleEditor } from './LinearScaleEditor';
import { GridEditor } from './GridEditor';
import type { FormField } from '../../types';

const CHOICE_TYPES: FormField['fieldType'][] = ['Select', 'MultiSelect'];

export interface BuilderField extends FormField {
  localId: string;
}

interface FormFieldEditorRowProps {
  field: BuilderField;
  onChange: (field: BuilderField) => void;
  onDelete: () => void;
  readOnly?: boolean;
}

export function FormFieldEditorRow({
  field,
  onChange,
  onDelete,
  readOnly = false,
}: FormFieldEditorRowProps) {
  const { t } = useTranslation('forms');
  const itemRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (readOnly) return undefined;
    const el = itemRef.current;
    const handle = handleRef.current;
    if (!el || !handle) return undefined;
    const cleanupDraggable = draggable({
      element: el,
      dragHandle: handle,
      getInitialData: () => ({ localId: field.localId }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
    const cleanupDrop = dropTargetForElements({
      element: el,
      getData: () => ({ localId: field.localId }),
    });
    return () => {
      cleanupDraggable();
      cleanupDrop();
    };
  }, [field.localId, readOnly]);

  return (
    <div
      ref={itemRef}
      className={cn('rounded-lg border p-3 space-y-3', isDragging && 'opacity-40')}
      data-testid={`forms-builder-field-row-${field.localId}`}
    >
      <div className="flex items-center gap-2">
        {!readOnly && (
          <button
            ref={handleRef}
            type="button"
            className="cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
            aria-label={t('builder.dragField')}
            data-testid={`forms-builder-field-drag-handle-${field.localId}`}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <span className="text-xs font-medium text-muted-foreground">
          {t(`builder.fieldTypes.${field.fieldType}`)}
        </span>
        <div className="flex-1" />
        {!readOnly && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onDelete}
                aria-label={t('builder.deleteField')}
                data-testid={`forms-builder-field-delete-${field.localId}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('builder.deleteField')}</TooltipContent>
          </Tooltip>
        )}
      </div>

      <Input
        value={field.label}
        onChange={(e) => onChange({ ...field, label: e.target.value })}
        disabled={readOnly}
        data-testid={`forms-builder-field-label-input-${field.localId}`}
      />

      {CHOICE_TYPES.includes(field.fieldType) && (
        <FieldOptionsEditor
          fieldId={field.localId}
          optionsJson={field.options}
          onChange={(optionsJson) => onChange({ ...field, options: optionsJson })}
          disabled={readOnly}
        />
      )}

      {field.fieldType === 'LinearScale' && (
        <LinearScaleEditor
          fieldId={field.localId}
          optionsJson={field.options}
          onChange={(optionsJson) => onChange({ ...field, options: optionsJson })}
          disabled={readOnly}
        />
      )}

      {field.fieldType === 'Grid' && (
        <GridEditor
          fieldId={field.localId}
          optionsJson={field.options}
          onChange={(optionsJson) => onChange({ ...field, options: optionsJson })}
          disabled={readOnly}
        />
      )}

      <div className="flex items-center justify-between">
        <span className="text-sm">{t('builder.requiredLabel')}</span>
        <Switch
          checked={field.required}
          disabled={readOnly}
          onCheckedChange={(checked) => onChange({ ...field, required: checked })}
          data-testid={`forms-builder-field-required-toggle-${field.localId}`}
        />
      </div>
    </div>
  );
}
